import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection, addDoc, updateDoc, doc, serverTimestamp,
  getDocs, query, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAllTaskTags } from '../../hooks/useCompanyData';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  TAG_COLOR_TOKENS, DEFAULT_TAG_COLOR, validateTagName, normalizeTagName, countTagUsage,
  DEFAULT_TASK_TAGS,
} from '../../utils/taskTags';
import { Plus, Edit, ToggleLeft, ToggleRight } from 'lucide-react';
import '../../components/common/TaskTagChips.css';
import './AdminTagsPage.css';

export default function AdminTagsPage() {
  const { t } = useTranslation();
  const { tags, loading } = useAllTaskTags();
  const { user, companyId } = useAuth();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [form, setForm] = useState({ name: '', color: DEFAULT_TAG_COLOR });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [usageCounts, setUsageCounts] = useState({});
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const seedDefaults = async () => {
    if (!companyId || seeding) return;
    setSeeding(true);
    try {
      const now = serverTimestamp();
      for (const tag of DEFAULT_TASK_TAGS) {
        await addDoc(collection(db, COLLECTIONS.TASK_TAGS), {
          companyId,
          name: tag.name,
          color: tag.color,
          isDefault: true,
          isActive: true,
          sortOrder: tag.sortOrder,
          createdBy: user.uid,
          createdAt: now,
          updatedAt: now,
        });
      }
      toast(t('tags.defaultsSeeded'), 'success');
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    if (!companyId) return;
    loadUsage();
  }, [companyId, tags.length]);

  const loadUsage = async () => {
    if (!companyId) return;
    setLoadingUsage(true);
    try {
      const snap = await getDocs(
        query(collection(db, COLLECTIONS.PRODUCTION_TASKS), where('companyId', '==', companyId))
      );
      const tasks = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const counts = {};
      for (const tag of tags) {
        counts[tag.id] = countTagUsage(tasks, tag.id);
      }
      setUsageCounts(counts);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingUsage(false);
    }
  };

  const sorted = useMemo(
    () => [...tags].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name)),
    [tags],
  );

  const activeCount = useMemo(() => tags.filter((tg) => tg.isActive).length, [tags]);

  const activeNames = useMemo(
    () => tags.filter((tg) => tg.id !== editTag?.id).map((tg) => tg.name),
    [tags, editTag],
  );

  const openAdd = () => {
    setEditTag(null);
    setForm({ name: '', color: DEFAULT_TAG_COLOR });
    setErrors({});
    setShowForm(true);
  };

  const openEdit = (tag) => {
    setEditTag(tag);
    setForm({ name: tag.name, color: tag.color || DEFAULT_TAG_COLOR });
    setErrors({});
    setShowForm(true);
  };

  const validate = () => {
    const e = {};
    const err = validateTagName(form.name, activeNames);
    if (err === 'required') e.name = t('validation.required');
    else if (err === 'tooLong') e.name = t('tags.nameTooLong');
    else if (err === 'duplicate') e.name = t('tags.duplicateName');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editTag) {
        const newName = normalizeTagName(form.name);
        await updateDoc(doc(db, COLLECTIONS.TASK_TAGS, editTag.id), {
          name: newName,
          color: form.color,
          updatedAt: serverTimestamp(),
        });
        if (newName !== editTag.name) {
          const tasksSnap = await getDocs(
            query(
              collection(db, COLLECTIONS.PRODUCTION_TASKS),
              where('companyId', '==', companyId),
              where('tagIds', 'array-contains', editTag.id),
            ),
          );
          if (!tasksSnap.empty) {
            const batch = writeBatch(db);
            tasksSnap.docs.forEach((taskDoc) => {
              const data = taskDoc.data();
              const ids = data.tagIds || [];
              const names = [...(data.tagNames || [])];
              const idx = ids.indexOf(editTag.id);
              if (idx >= 0) names[idx] = newName;
              batch.update(taskDoc.ref, { tagNames: names, updatedAt: serverTimestamp() });
            });
            await batch.commit();
          }
        }
      } else {
        const maxOrder = tags.reduce((m, tg) => Math.max(m, tg.sortOrder || 0), 0);
        await addDoc(collection(db, COLLECTIONS.TASK_TAGS), {
          companyId,
          name: normalizeTagName(form.name),
          color: form.color,
          isDefault: false,
          isActive: true,
          sortOrder: maxOrder + 1,
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      toast(t(editTag ? 'tags.updated' : 'tags.created'), 'success');
      setShowForm(false);
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = (tag) => {
    const usage = usageCounts[tag.id] || 0;
    if (tag.isActive && usage > 0) {
      setArchiveTarget(tag);
      return;
    }
    performToggle(tag);
  };

  const performToggle = async (tag) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.TASK_TAGS, tag.id), {
        isActive: !tag.isActive,
        updatedAt: serverTimestamp(),
      });
      toast(tag.isActive ? t('tags.archivedSuccess') : t('tags.restoredSuccess'), 'success');
      setArchiveTarget(null);
    } catch {
      toast(t('toast.saveFailed'), 'error');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{t('tags.pageTitle')}</h1>
          <p className="page-header-sub">{t('tags.pageSubtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={18} />{t('tags.addTag')}
        </button>
      </div>

      <div className="page-content-wide">
        <div className="card">
          <div className="card-header">
            <div>
              <span className="card-title">{t('tags.allTags')}</span>
              <p className="text-sm text-muted" style={{ marginTop: 2 }}>
                {t('tags.allTagsHint')}
              </p>
            </div>
            {!loading && (
              <span className="text-sm text-muted">
                {activeCount} active · {sorted.length} total
              </span>
            )}
          </div>
          {loading ? (
            <div className="page-loading" style={{ minHeight: 200 }}><div className="spinner" /></div>
          ) : sorted.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-8)' }}>
              <p className="text-sm text-muted">{t('tags.noTags')}</p>
              <button className="btn btn-secondary" style={{ marginTop: 'var(--space-4)' }} onClick={seedDefaults} disabled={seeding}>
                {seeding ? <span className="spinner" /> : t('tags.loadDefaults')}
              </button>
            </div>
          ) : (
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table tags-table">
                <thead>
                  <tr>
                    <th>{t('tags.tagName')}</th>
                    <th className="col-color">{t('tags.color')}</th>
                    <th className="col-usage">{t('tags.usage')}</th>
                    <th className="col-default">{t('tags.isDefault')}</th>
                    <th className="col-status">{t('tags.status')}</th>
                    <th className="col-actions">{t('taskTable.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((tag) => (
                    <tr key={tag.id} className={!tag.isActive ? 'row-inactive' : ''}>
                      <td>
                        <span className={`task-tag-chip task-tag-chip--${tag.color || 'slate'}`}>
                          {tag.name}
                        </span>
                      </td>
                      <td className="col-color text-sm text-muted">{t(`tags.colors.${tag.color || 'slate'}`)}</td>
                      <td className="col-usage text-sm text-muted">
                        {loadingUsage ? '…' : `${usageCounts[tag.id] || 0}×`}
                      </td>
                      <td className="col-default">
                        {tag.isDefault
                          ? <span className="badge badge-active">{t('tags.default')}</span>
                          : <span className="text-muted text-xs">—</span>}
                      </td>
                      <td className="col-status">
                        <button
                          className="btn btn-icon"
                          onClick={() => handleToggleActive(tag)}
                          title={tag.isActive ? t('tags.archive') : t('tags.restore')}
                          aria-label={tag.isActive ? t('tags.archive') : t('tags.restore')}
                        >
                          {tag.isActive
                            ? <ToggleRight size={20} style={{ color: 'var(--color-brand)' }} />
                            : <ToggleLeft size={20} style={{ color: 'var(--text-muted)' }} />}
                        </button>
                      </td>
                      <td className="col-actions">
                        <button
                          className="btn btn-icon btn-sm"
                          onClick={() => openEdit(tag)}
                          title={t('tags.editTag')}
                          aria-label={t('tags.editTag')}
                        >
                          <Edit size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <Modal
          title={editTag ? t('tags.editTag') : t('tags.addTag')}
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" /> : t('common.save')}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">{t('tags.tagName')} <span className="required">*</span></label>
            <input
              className={`form-control ${errors.name ? 'is-error' : ''}`}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={40}
              autoFocus
            />
            {errors.name && <p className="form-error">{errors.name}</p>}
          </div>
          <div className="form-group">
            <label className="form-label">{t('tags.color')}</label>
            <div className="tag-color-picker">
              {TAG_COLOR_TOKENS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`tag-color-swatch task-tag-chip task-tag-chip--${color}${form.color === color ? ' is-selected' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, color }))}
                  title={t(`tags.colors.${color}`)}
                  aria-label={t(`tags.colors.${color}`)}
                  aria-pressed={form.color === color}
                />
              ))}
            </div>
          </div>
        </Modal>
      )}

      {archiveTarget && (
        <ConfirmDialog
          title={t('tags.archiveConfirmTitle')}
          message={t('tags.archiveConfirmMessage', {
            name: archiveTarget.name,
            count: usageCounts[archiveTarget.id] || 0,
          })}
          confirmLabel={t('tags.archive')}
          onConfirm={() => performToggle(archiveTarget)}
          onCancel={() => setArchiveTarget(null)}
        />
      )}
    </div>
  );
}
